/** Las sucursales limpias: con nombre y dirección, como mucho ocho. */
export function normalizeLocations(raw) {
    if (!Array.isArray(raw))
        return [];
    const text = (value, max) => (typeof value === 'string' ? value.trim().slice(0, max) : '');
    return raw.flatMap((entry) => {
        const item = (entry && typeof entry === 'object' ? entry : {});
        const address = text(item.address, 200);
        if (!address)
            return [];
        const name = text(item.name, 60) || address.split(',')[0].trim();
        const phone = text(item.phone, 40);
        const hours = text(item.hours, 80);
        return [{ name, address, ...(phone ? { phone } : {}), ...(hours ? { hours } : {}) }];
    }).slice(0, 8);
}
const PHONE = /^[+(]?\d[\d\s().-]{6,}$/;
const DAYS = /\b(mon|tue|wed|thu|fri|sat|sun|lun|mar|mi[eé]|jue|vie|s[aá]b|dom|daily|diario)/i;
const TIME = /\d{1,2}(:\d{2})?\s*(am|pm|h\b)|\d{1,2}:\d{2}/i;
const ICON_PART = { map: 'address', mapPin: 'address', phone: 'phone', clock: 'hours' };
const plain = (node) => (typeof node.content === 'string' ? node.content.replace(/<[^>]*>/g, '').trim() : '');
const slotOf = (node) => (typeof node.aiSlot === 'string' ? node.aiSlot : '');
const isText = (node) => node.type === 'text' || node.type === 'heading' || node.type === 'paragraph';
const isMap = (node) => node.type === 'embed' && /(^|&)(zoom|type|q)=/.test(typeof node.content === 'string' ? node.content : '');
function partOf(node, siblingIcon) {
    if (isMap(node))
        return 'map';
    if (node.type === 'link' || node.type === 'button')
        return 'directions';
    if (!isText(node))
        return 'other';
    const slot = slotOf(node);
    if (slot === 'headline' || slot === 'repeat.item.headline' || slot === 'repeat.item.title')
        return 'name';
    if (siblingIcon && ICON_PART[siblingIcon])
        return ICON_PART[siblingIcon];
    const text = plain(node);
    if (PHONE.test(text))
        return 'phone';
    if (DAYS.test(text) && TIME.test(text))
        return 'hours';
    return 'address';
}
const mapsSearch = (address) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
/** El ajuste del mapa con su dirección: `q=…` delante de lo que traía (zoom, tipo). */
function mapContent(current, address) {
    const rest = current.split('&').filter((pair) => pair && !pair.startsWith('q='));
    return [`q=${encodeURIComponent(address)}`, ...rest].join('&');
}
/** Una tarjeta llena con su sucursal; lo que no tiene dato, fuera. */
function fillCard(card, location) {
    const drop = new Set();
    const visit = (node, siblingIcon) => {
        const part = partOf(node, siblingIcon);
        if (part === 'name')
            node.content = location.name;
        else if (part === 'address')
            node.content = location.address;
        else if (part === 'phone') {
            if (location.phone)
                node.content = location.phone;
            else
                drop.add(node);
        }
        else if (part === 'hours') {
            if (location.hours)
                node.content = location.hours;
            else
                drop.add(node);
        }
        else if (part === 'map')
            node.content = mapContent(typeof node.content === 'string' ? node.content : '', location.address);
        else if (part === 'directions') {
            const loose = node;
            loose.interaction = { ...loose.interaction, action: 'link', href: mapsSearch(location.address), target: '_blank' };
            if (typeof loose.href === 'string')
                loose.href = mapsSearch(location.address);
        }
        /* Las filas con icono (el pin, el teléfono, el reloj): el texto de al lado se reconoce por su icono. */
        const icon = node.children.find((child) => child.type === 'icon');
        const iconName = typeof icon?.iconName === 'string' ? icon.iconName : undefined;
        node.children.forEach((child) => visit(child, iconName));
    };
    visit(card, undefined);
    if (drop.size) {
        const prune = (node) => {
            const before = node.children.length;
            node.children = node.children.filter((child) => !drop.has(child) && !prune(child));
            /* Una fila que se quedó sólo con su icono tampoco sirve. */
            const onlyIcon = node.children.length > 0 && node.children.every((child) => child.type === 'icon');
            return before > 0 && (node.children.length === 0 || (onlyIcon && node.children.length < before));
        };
        prune(card);
    }
    return card;
}
/**
 * Las sucursales en un bloque de varias: una tarjeta por cada una (copiando
 * la primera) y, si hay un mapa grande fuera de las tarjetas, con la primera.
 * Sin sucursales, no se toca nada.
 */
export function fillLocations(roots, locations, cloneNode) {
    if (!locations.length)
        return roots;
    let filled = false;
    const visit = (node) => {
        if (!filled && slotOf(node) === 'repeat.list' && node.children.length) {
            const prototype = node.children[0];
            node.children = locations.map((location) => fillCard(cloneNode(prototype), location));
            filled = true;
            return;
        }
        if (!filled)
            node.children.forEach(visit);
    };
    roots.forEach(visit);
    /* El mapa grande de al lado de la lista: el de la primera sucursal. */
    const bigMaps = (node, insideList) => {
        if (slotOf(node) === 'repeat.list')
            return;
        if (!insideList && isMap(node))
            node.content = mapContent(typeof node.content === 'string' ? node.content : '', locations[0].address);
        node.children.forEach((child) => bigMaps(child, insideList));
    };
    roots.forEach((root) => bigMaps(root, false));
    return roots;
}
//# sourceMappingURL=locations.js.map